import { type FC, useMemo, useState } from 'react';

import PrimaryProviderAssignmentPanel from '@/components/PrimaryProviderAssignmentPanel';
import SessionAttendanceChips, {
  getSessionSelectionSummary
} from '@/components/SessionAttendanceChips';
import { DAY_LABELS } from '@/constants/dayOfWeek';
import usePrimaryProviderAssignment from '@/hooks/usePrimaryProviderAssignment';
import { Button, Pill } from '@/ui';
import Select from '@/ui/components/Form/Select';
import Modal from '@/ui/components/Modal';
import { formatTimeOfDay } from '@/utils/dates';
import {
  displayAsOf,
  lettersForSessionIds,
  sessionLetterMap,
  sessionsInEffectAsOf,
  uniqueCounselors
} from '@/utils/treatmentGroupSessions';

import type { Props } from './types';

const AddToGroupModal: FC<Props> = ({
  group,
  isPending,
  manage,
  onClose,
  onConfirm,
  open,
  timeZone
}) => {
  const relationshipType = group.relationship_type;
  const counselors = useMemo(() => uniqueCounselors(group.sessions), [group.sessions]);

  const [selectedStaffId, setSelectedStaffId] = useState<string>(counselors[0]?.staff_id ?? '');
  const [setPrimaryProvider, setSetPrimaryProvider] = useState<boolean>(false);

  // Scoped to the group's currently-effective sessions (as of displayAsOf,
  // not just this week, so a group whose start date is still in the
  // future still offers its intended schedule) -- only those have a
  // letter the server-side letter map will actually resolve, so a session
  // outside it (not yet started, or already ended) isn't offered here.
  // Also drops a session that's already scheduled to be removed (a set
  // effective_until): there's no point letting a member be (re)assigned to
  // something that's going away, so only fully-effective sessions show up.
  const scheduledSessions = useMemo(
    () =>
      sessionsInEffectAsOf(group.sessions, displayAsOf(group)).filter(
        (session) => !session.effective_until
      ),
    [group]
  );
  const letterMap = useMemo(
    () =>
      sessionLetterMap(
        scheduledSessions.map((session) => ({
          id: session.id,
          dayOfWeek: session.day_of_week,
          startTime: session.start_time
        }))
      ),
    [scheduledSessions]
  );
  const sessionOptions = useMemo(
    () =>
      scheduledSessions.map((session) => ({
        id: session.id,
        label: `${DAY_LABELS[session.display_day_of_week]} · ${formatTimeOfDay(session.display_start_time)}`
      })),
    [scheduledSessions]
  );
  const [selectedSessionIds, setSelectedSessionIds] = useState<string[]>(
    () => manage?.initialSessionIds ?? sessionOptions.map((session) => session.id)
  );
  const sessionSummary = getSessionSelectionSummary(selectedSessionIds, sessionOptions);
  const noSessionsSelected = sessionOptions.length > 0 && selectedSessionIds.length === 0;

  const selectedCounselor = counselors.find((c) => c.staff_id === selectedStaffId);
  const selectedProviderName = selectedCounselor?.staff_name ?? '';

  const {
    canSetPrimary,
    currentRelationships,
    currentStaffNames,
    isSelectedProviderCurrent,
    tooManyPrimaries
  } = usePrimaryProviderAssignment(relationshipType, selectedStaffId);

  const textContent = (
    <div className="text-left">
      {!manage && (
        <p className="mb-4">
          Add this member to <span className="font-bold">{group.name}</span>?
        </p>
      )}
      {!manage && counselors.length > 0 && (
        <Select
          label="Provider"
          placeholder="Select a provider"
          options={counselors.map((counselor) => ({
            label: counselor.staff_name,
            value: counselor.staff_id
          }))}
          onChange={(option) => {
            setSelectedStaffId(String(option.value));
            setSetPrimaryProvider(false);
          }}
          selectedOption={
            selectedCounselor
              ? { value: selectedCounselor.staff_id, label: selectedCounselor.staff_name }
              : { value: '', label: '' }
          }
        />
      )}
      {sessionOptions.length > 0 && (
        <div className="mt-4 rounded-lg border border-uniform-gray-200 bg-uniform-gray-50 p-4 dark:border-uniform-gray-700 dark:bg-uniform-gray-800">
          <div className="mb-1 flex items-center justify-between gap-3">
            <span className="text-xs font-semibold uppercase tracking-wide text-uniform-gray-500 dark:text-uniform-gray-400">
              Sessions
            </span>
            <Pill
              color={sessionSummary.isAllSelected ? 'neutral' : 'primary'}
              size="sm"
              variant="subtle">
              {sessionSummary.label}
            </Pill>
          </div>
          <SessionAttendanceChips
            label={
              <span className="text-sm text-uniform-gray-600 dark:text-uniform-gray-400">
                Sessions this member will attend
              </span>
            }
            onChange={setSelectedSessionIds}
            selectedIds={selectedSessionIds}
            sessions={sessionOptions}
          />
          <p className="mt-1 text-xs text-uniform-gray-400">Click a session to remove it.</p>
          {noSessionsSelected ? (
            <p className="mt-3 text-xs text-uniform-red-600 dark:text-uniform-red-400">
              Select at least one session.
            </p>
          ) : (
            <p className="mt-3 text-xs text-uniform-gray-400">
              Times shown in {timeZone ?? group.timezone}.
            </p>
          )}
        </div>
      )}
      {!manage && relationshipType && (
        <PrimaryProviderAssignmentPanel
          canSetPrimary={canSetPrimary}
          checked={setPrimaryProvider}
          currentRelationshipsCount={currentRelationships.length}
          currentStaffNames={currentStaffNames}
          isSelectedProviderCurrent={isSelectedProviderCurrent}
          onCheckedChange={setSetPrimaryProvider}
          relationshipType={relationshipType}
          selectedProviderName={selectedProviderName}
          tooManyPrimaries={tooManyPrimaries}
        />
      )}
      {manage && (
        <Button
          className="mt-4 w-full"
          color="destructive"
          isLoading={isPending}
          onClick={manage.onRemove}
          type="button"
          variant="outline">
          Remove from Group
        </Button>
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={manage ? 'Manage Membership' : 'Add to Group'}
      textContent={textContent}
      blurredBackground
      button={{
        children: manage ? 'Save changes' : 'Add to Group',
        title: manage ? 'Save changes' : 'Add to Group',
        ariaLabel: manage ? 'Save changes' : 'Add to Group',
        disabled: isPending || noSessionsSelected,
        loading: isPending,
        onClick: () => {
          const partialSessionLetters =
            sessionOptions.length > 0 ? lettersForSessionIds(selectedSessionIds, letterMap) : [];
          if (manage) {
            manage.onSave(partialSessionLetters);
            return;
          }
          onConfirm({
            staff_id: selectedStaffId || undefined,
            ...(relationshipType ? { set_primary_provider: setPrimaryProvider } : {}),
            ...(sessionOptions.length > 0 ? { partial_session_letters: partialSessionLetters } : {})
          });
        }
      }}
    />
  );
};

export default AddToGroupModal;
